import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Search, Zap, ChevronDown, User } from "lucide-react";
import { useAuth, homeFor } from "@/lib/auth";
import { useLoc } from "@/lib/location";
import { LocationPicker } from "./LocationPicker";

export const Logo = ({ to = "/" }) => (
  <Link to={to} data-testid="logo-link" className="flex items-center gap-1.5 shrink-0">
    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 grid place-items-center"><Zap className="w-5 h-5 text-white fill-white" /></span>
    <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">SuperDeals<span className="text-red-500">.world</span></span>
  </Link>
);

export function Navbar() {
  const { user } = useAuth();
  const { loc, radius } = useLoc();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  return (
    <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <Logo />
        <button data-testid="nav-location-button" onClick={() => setOpen(true)} className="hidden md:flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 max-w-[220px]">
          <MapPin className="w-4 h-4 text-red-500 shrink-0" /><span className="truncate font-medium">{loc.label}</span><span className="text-slate-400">· {radius < 1 ? `${radius * 1000}m` : `${radius}km`}</span><ChevronDown className="w-3 h-3" />
        </button>
        <form onSubmit={(e) => { e.preventDefault(); nav(`/deals?q=${encodeURIComponent(q)}`); }} className="hidden lg:flex flex-1 max-w-md items-center gap-2 bg-slate-100 rounded-full px-4 h-10">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals, stores, areas…" className="bg-transparent flex-1 text-sm outline-none" />
        </form>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="nav-location-mobile" onClick={() => setOpen(true)} className="md:hidden p-2 rounded-full border border-slate-200"><MapPin className="w-4 h-4 text-red-500" /></button>
          {user ? (
            <Link data-testid="nav-account-link" to={homeFor(user.role)} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold">
              <User className="w-4 h-4" /> <span className="hidden sm:inline">{user.role === "customer" ? "My Coupons" : "Dashboard"}</span>
            </Link>
          ) : (
            <>
              <Link data-testid="nav-login-link" to="/login" className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 whitespace-nowrap">Log in</Link>
              <Link data-testid="nav-seller-register-link" to="/seller/register" className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold">
                <span className="hidden sm:inline">List your store</span><span className="sm:hidden">Sell</span>
              </Link>
            </>
          )}
        </div>
      </div>
      <LocationPicker open={open} onOpenChange={setOpen} />
    </header>
  );
}

export const Footer = () => (
  <footer className="bg-slate-950 text-slate-400 mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
      <div className="col-span-2 md:col-span-1 space-y-3">
        <div className="font-heading font-extrabold text-white text-lg">SuperDeals<span className="text-red-500">.world</span></div>
        <p>Discover Nearby Deals. Claim in Seconds. Save Locally.</p>
      </div>
      <div className="space-y-2"><p className="text-white font-semibold">Explore</p><Link to="/deals" className="block hover:text-white">All deals</Link><Link to="/#categories" className="block hover:text-white">Categories</Link><Link to="/customer" className="block hover:text-white">My coupons</Link></div>
      <div className="space-y-2"><p className="text-white font-semibold">Merchants</p><Link to="/seller/register" className="block hover:text-white">Become a seller</Link><Link to="/login" className="block hover:text-white">Seller login</Link><Link to="/#packages" className="block hover:text-white">Packages</Link></div>
      <div className="space-y-2"><p className="text-white font-semibold">Legal</p><Link data-testid="footer-privacy" to="/privacy" className="block hover:text-white">Privacy policy</Link><Link data-testid="footer-terms" to="/terms" className="block hover:text-white">Terms of use</Link><Link to="/contact" className="block hover:text-white">Contact</Link></div>
    </div>
    <div className="border-t border-slate-800 py-5 text-center text-xs">© 2026 SuperDeals.world. All rights reserved.</div>
  </footer>
);
